// Importing React/Redux/Third party Library components
import React from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import Media from 'react-media'
// Importing common module component
import Button from 'portal-modules/common/components/Button'
import { RpModal } from 'portal-modules/common/components/modals'
import CustomContent from 'portal-modules/common/components/CustomContent'
// Importing required components
import SepaDisclaimerText from 'portal-modules/payments/components/SepaDisclaimerText'
// Importing constant
import { COUNTRIES } from 'portal-modules/vehicles/constants/states'
import { PAYMENT_TYPES } from 'portal-modules/payments/constants'
// Importing utility function
import { getFormattedDateTime } from 'portal-modules/common/utils/get-formatted-date-time'
import displayMaskedNumber from 'portal-modules/common/utils/display-masked-number'
// Importing Styled Components
import {
  StyledSepaDiv,
  StyledSepaHeader,
  StyledSepaContent,
  StyledSepaSection,
  StyledSepaLabel,
  StyledSepaValue,
  StyledSepaSpan,
} from 'portal-modules/common/components/cards/atoms'

// Styled Sepa Row Component
const StyledSepaRow = ({ title, value }) => (
  <StyledSepaSection>
    <StyledSepaLabel>{title}</StyledSepaLabel>
    <StyledSepaValue>{value}</StyledSepaValue>
  </StyledSepaSection>
)

// Functional Component: Sepa Mandate Modal
const SepaMandateModal = ({
  settings,
  selectedId,
  paymentMethods,
  sepaMandateCustomText,
  propertyLocaleCode,
  onClose,
  ...rest
}) => {
  const {
    debtor_address_line_1 = '',
    debtor_address_line_2 = '',
    debtor_city = '',
    debtor_state_code = '',
    debtor_postal_code = '',
    debtor_country_code,
    debtor_country_name = '',
    reference_id,
    debtor_name,
    debtor_email,
    iban = '',
    bic,
    signature_data,
    signed_on,
    sepa_worldpay_mandate_prefix = '',
  } =
    paymentMethods &&
    Object.values(paymentMethods)
      ?.map(item => item)
      ?.find(({ id }) => id == selectedId)?.sepa_info

  const countryName =
    debtor_country_name ||
    COUNTRIES?.filter(item => item?.value == debtor_country_code)[0]?.name ||
    ''
  // TODO : The star needs to be converted from the BackEnd Only.
  // However, As there are multiple places which are going to affect in backend so we are adding this logic for now.
  const ibanWithDot = displayMaskedNumber(PAYMENT_TYPES.SEPA.value)(
    iban.toString().replaceAll('*', 'X'),
  )
  return (
    <Media query='(max-width:767px)'>
      {smallScreen => (
        <RpModal
          {...rest}
          title={__('Sepa Mandate')}
          footerContent={
            <Button
              size='large'
              fullWidth
              appearance='primary'
              onClick={onClose}
            >
              {__('Close')}
            </Button>
          }
          isWideDialog={true}
        >
          {sepaMandateCustomText ? (
            <CustomContent>{sepaMandateCustomText}</CustomContent>
          ) : (
            <></>
          )}
          <StyledSepaDiv>
            <StyledSepaHeader>
              {__('Sepa Account Information')}
            </StyledSepaHeader>
          </StyledSepaDiv>
          <StyledSepaContent>
            <StyledSepaRow
              title={__('Payment Type')}
              value={__('Sepa Direct Debit')}
            />
            {settings?.company_name ? (
              <StyledSepaRow
                title={__('Creditor Name')}
                value={settings?.company_name}
              />
            ) : (
              <></>
            )}
            {settings?.creditor_id && smallScreen ? (
              <>
                <StyledSepaSection noMargin>
                  <StyledSepaLabel>{__('Creditor ID')}</StyledSepaLabel>
                </StyledSepaSection>
                <StyledSepaSection idMargin>
                  <StyledSepaValue>{settings?.creditor_id}</StyledSepaValue>
                </StyledSepaSection>
              </>
            ) : (
              <></>
            )}
            {settings?.creditor_id && !smallScreen ? (
              <StyledSepaRow
                title={__('Creditor ID')}
                value={settings?.creditor_id}
              />
            ) : (
              <></>
            )}
            {settings?.company_address && smallScreen ? (
              <StyledSepaSection>
                <StyledSepaLabel labelMargin>
                  {__('Creditor Address')}
                </StyledSepaLabel>
                <StyledSepaSection>
                  <StyledSepaValue addressAlign>
                    {settings?.company_address}
                  </StyledSepaValue>
                </StyledSepaSection>
              </StyledSepaSection>
            ) : (
              <></>
            )}
            {settings?.company_address && !smallScreen ? (
              <StyledSepaRow
                title={__('Creditor Address')}
                value={settings?.company_address}
              />
            ) : (
              <></>
            )}
            <StyledSepaRow
              title={__('Mandate Reference')}
              value={sepa_worldpay_mandate_prefix || reference_id}
            />
          </StyledSepaContent>
          <StyledSepaDiv>
            <StyledSepaHeader>
              {__('Account Holder Information')}
            </StyledSepaHeader>
          </StyledSepaDiv>
          <StyledSepaRow
            title={__('Account Holder Name')}
            value={debtor_name}
          />
          <StyledSepaRow title={__('Email Address')} value={debtor_email} />
          <StyledSepaRow title={__('IBAN')} value={ibanWithDot} />
          <StyledSepaRow title={__('BIC')} value={bic} />
          <StyledSepaRow
            title={__('Country')}
            value={countryName || debtor_country_code}
          />
          <StyledSepaSection>
            <StyledSepaLabel>{__('Address')}</StyledSepaLabel>
            <StyledSepaValue addressMargin addressAlign>
              {[
                debtor_address_line_1,
                debtor_address_line_2,
                debtor_city,
                debtor_state_code,
                debtor_postal_code,
              ]
                .join(', ')
                .replaceAll(' ,', '')}
            </StyledSepaValue>
          </StyledSepaSection>
          <StyledSepaRow
            title={__('Payment Frequency')}
            value={__('Recurrent')}
          />
          <StyledSepaDiv />
          <SepaDisclaimerText companyName={settings?.company_name} />
          <StyledSepaContent>
            <StyledSepaHeader>
              {__(
                'Resident Agreed to the Terms & Conditions on {%s, 0} @ {%s, 1}',
                [
                  getFormattedDateTime({
                    dateTime: signed_on,
                    isYearRequired: true,
                    isFullMonthName: false,
                    propertyLocaleCode,
                  }),
                  i18nUtils.datetime(signed_on, 'TIME_SHORT'),
                ],
              )}
            </StyledSepaHeader>
          </StyledSepaContent>
          <StyledSepaContent>
            <StyledSepaHeader>
              {__('Account Holder E-signature:')}
              <StyledSepaSpan>{signature_data}</StyledSepaSpan>
              {__('signed {%s, 0} @ {%s, 1}', [
                getFormattedDateTime({
                  dateTime: signed_on,
                  isYearRequired: true,
                  isFullMonthName: false,
                  propertyLocaleCode,
                }),
                i18nUtils.datetime(signed_on, 'TIME_SHORT'),
              ])}
            </StyledSepaHeader>
          </StyledSepaContent>
        </RpModal>
      )}
    </Media>
  )
}

// Prop Types for Sepa Mandate Modal Component
SepaMandateModal.PropTypes = {
  settings: PropTypes.object,
  selectedId: PropTypes.string,
  paymentMethods: PropTypes.array,
  sepaMandateCustomText: PropTypes.string,
  propertyLocaleCode: PropTypes.string,
  onClose: PropTypes.func,
}

// Connects the component to the Redux store, allowing it to access state and dispatch actions
export default connect(
  ({ user: { propertyId }, settings, paymentMethods, errors, properties }) => ({
    error: errors?.paymentMethods || '',
    settings: settings?.paymentMethods && settings?.paymentMethods.sepa,
    sepaMandateCustomText:
      settings?.paymentMethods?.sepa_mandate_custom_text || '',
    paymentMethods,
    propertyLocaleCode:
      properties && properties[propertyId]?.property_locale_code,
  }),
)(SepaMandateModal)
